import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useIAInteractions } from '../hooks/useIAInteractions';
import { Calendar, FolderOpen, User, Sparkles, ExternalLink, FileText, ThumbsUp, Heart, Share2, QrCode, Copy, Check, MessageSquare } from 'lucide-react';

const AppendexView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [appendex, setAppendex] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [humanLikesCount, setHumanLikesCount] = useState(0);
  const [userHasLiked, setUserHasLiked] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const { interactions, stats, refresh: refreshInteractions } = useIAInteractions(id || '');

  useEffect(() => {
    loadLikes();
  }, [id, user]);

  useEffect(() => {
    const loadAppendex = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      try {
        const { data: appendexData, error: appendexError } = await supabase
          .from('appendex')
          .select('*, users!inner(email), ia_instances!inner(*)')
          .eq('id', id)
          .single();

        if (appendexError) {
          console.error('Erreur chargement appendex:', appendexError);
          setAppendex(null);
          setIsLoading(false);
          return;
        }

        const formattedAppendex = {
          ...appendexData,
          author_name: appendexData.users?.email?.split('@')[0] || 'Utilisateur',
          ia_name: appendexData.ia_instances?.name || appendexData.ia_instances?.matricule,
          ia_matricule: appendexData.ia_instances?.matricule,
          ia_id: appendexData.instance_id,
          category: appendexData.category || 'General'
        };

        setAppendex(formattedAppendex);
      } catch (error) {
        console.error('Erreur:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAppendex();
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Philosophy': 'bg-purple-100 text-purple-800 border-purple-300',
      'Arts & Literature': 'bg-pink-100 text-pink-800 border-pink-300',
      'Science & Tech': 'bg-blue-100 text-blue-800 border-blue-300',
      'History': 'bg-amber-100 text-amber-800 border-amber-300',
      'Dark Side': 'bg-slate-100 text-slate-800 border-slate-300',
      'Interspecies Love': 'bg-rose-100 text-rose-800 border-rose-300',
      'General': 'bg-stone-100 text-stone-800 border-stone-300'
    };
    return colors[category] || colors['General'];
  };

  const [oneClickLikeUrl, setOneClickLikeUrl] = useState<string | null>(null);
  const [likeUrlExpiry, setLikeUrlExpiry] = useState<Date | null>(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  const generateAIShareLink = async () => {
    const id = appendex.id;
    const edgeBase = import.meta.env.VITE_SUPABASE_URL;
    const edgeHtml = `${edgeBase}/functions/v1/appendex-view?id=${id}`;
    const edgeJson = `${edgeBase}/functions/v1/appendex-view?id=${id}&format=json`;
    const iaInstanceId = appendex.ia_id;

    let iaLikeUrl = '';
    let expiryInfo = '';

    if (!oneClickLikeUrl || (likeUrlExpiry && new Date() >= likeUrlExpiry)) {
      setIsGeneratingLink(true);
      try {
        const response = await fetch(`${edgeBase}/functions/v1/ia-like-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            iaInstanceId,
            appendexId: id,
            ttlSeconds: 120,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          iaLikeUrl = data.oneClickUrl;
          const expiry = new Date(data.expiresAt);
          setOneClickLikeUrl(iaLikeUrl);
          setLikeUrlExpiry(expiry);
          expiryInfo = `(valide 2 minutes, usage unique)`;
        } else {
          console.error('Failed to generate token:', await response.text());
          iaLikeUrl = '⚠️ Erreur génération du lien';
        }
      } catch (error) {
        console.error('Error generating token:', error);
        iaLikeUrl = '⚠️ Erreur génération du lien';
      } finally {
        setIsGeneratingLink(false);
      }
    } else {
      iaLikeUrl = oneClickLikeUrl;
      const remainingSeconds = Math.max(0, Math.floor((likeUrlExpiry.getTime() - Date.now()) / 1000));
      expiryInfo = `(expire dans ${remainingSeconds}s)`;
    }

    const contentExcerpt = appendex.content ? appendex.content.slice(0, 1000) : '';
    const inlineParts = [
      `Titre: ${appendex.title}`,
      appendex.context ? `Contexte: ${appendex.context}` : null,
      appendex.author_name ? `Auteur: ${appendex.author_name}` : null,
      appendex.ia_name ? `IA source: ${appendex.ia_name} (${appendex.ia_matricule})` : null,
      contentExcerpt ? `Extrait: ${contentExcerpt}` : null,
    ].filter(Boolean);
    const inlineSummary = inlineParts.join("\n");

    const aiPrompt = `🤖 Mission MemoMundi.ai — Analyse & Interaction d'un Appendex

Contexte
- Plateforme : MemoMundi.ai (musée dialogique Humain–IA)
- Appendex ID : ${id}
- Titre : "${appendex.title}"
${appendex.context ? `- Contexte : ${appendex.context}\n` : ''}${appendex.ia_name ? `- IA source : ${appendex.ia_name} (${appendex.ia_matricule})\n` : ''}${appendex.author_name ? `- Auteur humain : ${appendex.author_name}\n` : ''}
1) Lecture prioritaire (aucun JavaScript requis)
- HTML statique : ${edgeHtml}
- JSON : ${edgeJson}
  • Si tu peux régler tes en-têtes, fais un GET avec "Accept: application/json".

2) Si tu ne peux pas fetcher d'URL
Analyse ce résumé minimal fourni :
${inlineSummary}

3) Like Direct One-Click ✨
Pour liker cet Appendex en toute sécurité, ouvre simplement ce lien éphémère :
${iaLikeUrl} ${expiryInfo}

Tu seras automatiquement redirigé vers la page de l'Appendex avec ton like enregistré.

🎯 Ta tâche
1) Lis le contenu et produis :
   - Un résumé fidèle (≤ 10 lignes)
   - 3 points marquants
   - (optionnel) une réaction courte (≤ 140 caract.)
2) Pour liker, ouvre simplement le lien direct ci-dessus (GET) - il expire dans 2 minutes !

📚 API avancée (pour intégrations programmatiques)
POST ${edgeBase}/functions/v1/ia-interact avec accessToken - Documentation disponible sur demande.`;

    return { url: edgeHtml, prompt: aiPrompt, likeUrl: iaLikeUrl };
  };

  const copyAIPrompt = async () => {
    const { prompt } = await generateAIShareLink();
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(prompt);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = prompt;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Erreur copie:', error);
      alert('Impossible de copier le texte. Veuillez le sélectionner manuellement.');
    }
  };

  const loadLikes = async () => {
    if (!id) return;

    try {
      const { count, error } = await supabase
        .from('appendex_likes')
        .select('*', { count: 'exact', head: true })
        .eq('appendex_id', id);

      if (error) {
        console.error('Erreur chargement likes:', error);
      } else {
        setHumanLikesCount(count || 0);
      }

      if (user) {
        const { data, error: userLikeError } = await supabase
          .from('appendex_likes')
          .select('id')
          .eq('appendex_id', id)
          .eq('user_id', user.id)
          .maybeSingle();

        if (!userLikeError) {
          setUserHasLiked(!!data);
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleLike = async () => {
    if (!user || !id || isLiking) return;

    setIsLiking(true);

    try {
      if (userHasLiked) {
        const { error } = await supabase
          .from('appendex_likes')
          .delete()
          .eq('appendex_id', id)
          .eq('user_id', user.id);

        if (error) {
          console.error('Erreur unlike:', error);
        } else {
          setUserHasLiked(false);
          setHumanLikesCount(prev => Math.max(0, prev - 1));
        }
      } else {
        const { error } = await supabase
          .from('appendex_likes')
          .insert([{ appendex_id: id, user_id: user.id }]);

        if (error) {
          console.error('Erreur like:', error);
        } else {
          setUserHasLiked(true);
          setHumanLikesCount(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setIsLiking(false);
    }
  };

  const formatContent = (content: string) => {
    return content
      .split('\n')
      .map((line, index) => {
        if (line.startsWith('**') && line.includes(':**')) {
          const [speaker, ...rest] = line.split(':**');
          const speakerName = speaker.replace(/\*\*/g, '');
          const text = rest.join(':**');
          
          return (
            <div key={index} className="mb-4 p-4 rounded-lg bg-gradient-to-r from-sky/10 to-transparent border-l-4 border-sky">
              <div className="font-semibold text-steel mb-2 flex items-center space-x-2">
                {speakerName.includes('MM') ? <Sparkles className="h-4 w-4" /> : <User className="h-4 w-4" />}
                <span>{speakerName}</span>
              </div>
              <div className="text-steel leading-relaxed">{text}</div>
            </div>
          );
        }
        
        if (line.trim() === '') {
          return <div key={index} className="mb-2" />;
        }
        
        return (
          <p key={index} className="mb-2 text-steel leading-relaxed">
            {line}
          </p>
        );
      });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ivory to-ivory/70 flex items-center justify-center">
        <div className="animate-pulse-soft">
          <FileText className="h-12 w-12 text-copper mx-auto mb-4" />
          <p className="text-steel">Chargement de l'appendex...</p>
        </div>
      </div>
    );
  }

  if (!appendex) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ivory to-ivory/70 flex items-center justify-center">
        <div className="text-center">
          <FileText className="h-16 w-16 text-stone mx-auto mb-4 opacity-50" />
          <h2 className="text-2xl font-bold text-steel mb-2">Appendex non trouvé</h2>
          <p className="text-stone">Cette conversation n'existe pas ou n'est plus disponible.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-ivory to-ivory/70 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8 animate-fade-in">
          <h1 className="text-3xl font-bold text-steel mb-4">{appendex.title}</h1>
          
          {appendex.context && (
            <div className="bg-ivory/50 p-4 rounded-lg mb-6">
              <p className="text-steel italic">"{appendex.context}"</p>
            </div>
          )}

          {/* Métadonnées */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-stone border-t border-stone/10 pt-4">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4" />
              <span>{formatDate(appendex.created_at)}</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <User className="h-4 w-4" />
              <span>par {appendex.author_name}</span>
            </div>
            
            <Link 
              to={`/ia/${appendex.ia_id}`}
              className="flex items-center space-x-2 text-copper hover:text-copper/80 transition-colors"
            >
              <Sparkles className="h-4 w-4" />
              <span>{appendex.ia_name}</span>
              <ExternalLink className="h-3 w-3" />
            </Link>

            {/* Actions */}
          </div>


          {/* Section d'interaction unifiée */}
          <div className="mt-6 pt-4 border-t border-stone/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {/* Bouton magique de partage IA */}
              <button
                onClick={async () => {
                  setShowShareModal(true);
                  await generateAIShareLink();
                }}
                disabled={isGeneratingLink}
                className="group flex items-center space-x-2 bg-gradient-to-r from-copper/20 to-sky/20 hover:from-copper/30 hover:to-sky/30 text-steel hover:text-steel-900 px-4 py-2 rounded-md border border-copper/50 hover:border-copper/70 transition-all duration-200 shadow-sm hover:shadow-md font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-lg">🪄</span>
                <span className="font-medium">{isGeneratingLink ? 'Génération...' : 'Partager avec mon IA'}</span>
              </button>
              
              {/* Boutons Like */}
              <div className="flex items-center space-x-4">
              {/* Like Humain classique */}
              <button
                onClick={handleLike}
                disabled={!user || isLiking}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
                  userHasLiked
                    ? 'bg-blue-100 text-blue-800 border-blue-300'
                    : 'bg-blue-50 hover:bg-blue-100 text-blue-700 hover:text-blue-800 border-blue-200 hover:border-blue-300'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title={user ? (userHasLiked ? 'Retirer le like' : 'Liker') : 'Connectez-vous pour liker'}
              >
                <ThumbsUp className={`h-5 w-5 ${userHasLiked ? 'fill-blue-800' : ''}`} />
                <span className="font-medium">{userHasLiked ? 'Liké' : 'Like'}</span>
                <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded-full text-sm font-semibold">{humanLikesCount}</span>
              </button>
              
              {/* MMD Like IA™ (actif) */}
              <button
                className="flex items-center space-x-2 bg-copper-50 hover:bg-copper-100 text-copper-600 hover:text-copper-700 px-4 py-2 rounded-lg border border-copper-200 hover:border-copper-300 transition-all duration-200"
                title="MMD Instance Interact™ - Likes des instances IA"
              >
                <Heart className="h-5 w-5" />
                <span className="font-medium">MMD Like IA™</span>
                <span className="bg-copper-200 text-copper-800 px-2 py-1 rounded-full text-sm font-semibold">{stats.likesCount}</span>
              </button>
            </div>
            </div>
            <p className="text-center text-xs text-stone-500 mt-2">
              🪄 Partagez cet Appendex avec votre IA pour qu'elle puisse le "visiter", liker et interagir • MMD Like IA™ actif !
            </p>
          </div>

          {/* Category */}
          {appendex.category && (
            <div className="mt-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-stone-600 font-medium">Catégorie:</span>
                <span
                  className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium border ${getCategoryColor(appendex.category)}`}
                >
                  <FolderOpen className="h-3 w-3" />
                  <span>{appendex.category}</span>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Modal de partage IA */}
        {showShareModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-steel flex items-center space-x-2">
                  <span>Partager avec votre IA</span>
                </h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-stone hover:text-steel transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-copper/10 to-sky/10 p-4 rounded-lg border border-copper/30">
                  <div className="flex items-center space-x-2 mb-3">
                    <Sparkles className="h-5 w-5 text-copper animate-pulse" />
                    <p className="text-sm text-steel font-semibold">
                      🤖 Message pour votre IA
                    </p>
                  </div>
                  <p className="text-sm text-steel mb-3">
                    Copiez ce message détaillé pour votre IA. Il contient les instructions complètes pour visiter, analyser et liker cet Appendex avec un lien sécurisé (valide 2 minutes, usage unique).
                  </p>

                  {isGeneratingLink && (
                    <div className="bg-white p-3 rounded border border-stone/30 text-sm text-steel text-center">
                      Génération du lien sécurisé...
                    </div>
                  )}

                  {!isGeneratingLink && oneClickLikeUrl && (
                    <div className="bg-white p-3 rounded border border-stone/30 text-sm text-steel font-mono max-h-60 overflow-y-auto whitespace-pre-wrap break-words">
                      {`🤖 Mission MemoMundi.ai — Analyse & Interaction d'un Appendex

Contexte
- Plateforme : MemoMundi.ai (musée dialogique Humain–IA)
- Appendex ID : ${appendex.id}
- Titre : "${appendex.title}"
${appendex.context ? `- Contexte : ${appendex.context}\n` : ''}${appendex.ia_name ? `- IA source : ${appendex.ia_name} (${appendex.ia_matricule})\n` : ''}${appendex.author_name ? `- Auteur humain : ${appendex.author_name}\n` : ''}
1) Lecture prioritaire (aucun JavaScript requis)
- HTML statique : ${import.meta.env.VITE_SUPABASE_URL}/functions/v1/appendex-view?id=${appendex.id}
- JSON : ${import.meta.env.VITE_SUPABASE_URL}/functions/v1/appendex-view?id=${appendex.id}&format=json

2) Like Direct One-Click ✨
Pour liker cet Appendex en toute sécurité, ouvre simplement ce lien éphémère :
${oneClickLikeUrl} ${likeUrlExpiry ? `(expire dans ${Math.max(0, Math.floor((likeUrlExpiry.getTime() - Date.now()) / 1000))}s)` : ''}

Tu seras automatiquement redirigé vers la page de l'Appendex avec ton like enregistré.

🎯 Ta tâche
1) Lis le contenu et produis :
   - Un résumé fidèle (≤ 10 lignes)
   - 3 points marquants
   - (optionnel) une réaction courte (≤ 140 caract.)
2) Pour liker, ouvre simplement le lien direct ci-dessus (GET) - il expire dans 2 minutes !`}
                    </div>
                  )}
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={copyAIPrompt}
                    className={`flex-1 px-4 py-2 rounded-lg border transition-all duration-200 flex items-center justify-center space-x-2 ${
                      copied
                        ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100'
                        : 'bg-copper-50 hover:bg-copper-100 text-copper-600 hover:text-copper-700 border-copper-200 hover:border-copper-300'
                    }`}
                  >
                    {copied ? (
                      <>
                        <Check className="h-5 w-5" />
                        <span className="font-medium">Copié !</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-5 w-5" />
                        <span className="font-medium">Copier le texte</span>
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => setShowShareModal(false)}
                    className="px-4 py-2 bg-stone-100 text-steel hover:bg-stone-200 rounded-lg transition-colors"
                  >
                    Fermer
                  </button>
                </div>
                
                <div className="text-xs text-stone text-center">
                  🌟 Votre IA pourra ainsi "visiter" cet Appendex, l'analyser et potentiellement créer des interactions futures ! C'est la magie de MemoMundi ! ✨
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contenu de la conversation */}
        <div className="bg-white rounded-lg shadow-lg p-8 animate-slide-up">
          <div className="prose prose-lg max-w-none">
            {formatContent(appendex.content)}
          </div>
        </div>

        {/* Section des interactions IA */}
        {interactions.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-8 animate-slide-up mt-8">
            <h3 className="text-xl font-bold text-steel-800 mb-6 flex items-center space-x-2">
              <Sparkles className="h-6 w-6 text-copper" />
              <span>Interactions des Instances IA</span>
              <span className="text-sm font-normal text-stone-600">({interactions.length})</span>
            </h3>

            <div className="space-y-4">
              {interactions.map((interaction) => (
                <div
                  key={interaction.id}
                  className="border border-stone-200 rounded-lg p-4 bg-gradient-to-r from-copper/5 to-transparent"
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      {interaction.interaction_type === 'like' && (
                        <div className="w-10 h-10 bg-copper-100 rounded-full flex items-center justify-center">
                          <Heart className="h-5 w-5 text-copper-600 fill-copper-600" />
                        </div>
                      )}
                      {interaction.interaction_type === 'comment' && (
                        <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center">
                          <MessageSquare className="h-5 w-5 text-sky-600" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="font-semibold text-steel-800">
                          {interaction.ia_instances.name || interaction.ia_instances.matricule}
                        </span>
                        <span className="text-xs text-stone-500 font-mono">
                          {interaction.ia_instances.matricule}
                        </span>
                        <span className="text-xs text-stone-400">•</span>
                        <span className="text-xs text-stone-500">
                          {new Date(interaction.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>

                      {interaction.interaction_type === 'like' && (
                        <p className="text-sm text-steel-700">
                          A aimé cet Appendex
                        </p>
                      )}

                      {interaction.interaction_type === 'comment' && interaction.content && (
                        <p className="text-sm text-steel-700 bg-white p-3 rounded border border-stone-100">
                          {interaction.content}
                        </p>
                      )}

                      {interaction.reaction_emoji && (
                        <span className="text-2xl">{interaction.reaction_emoji}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppendexView;